// src/components/tasks/TaskDetailModal.tsx
// Modal for viewing and editing task assignment details
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IonIcon, IonSpinner, IonAlert } from '@ionic/react';
import {
  calendarOutline,
  timeOutline,
  documentTextOutline,
  attachOutline,
  cameraOutline,
  imageOutline,
  trashOutline,
  close,
  checkmark,
  downloadOutline,
  starOutline,
  todayOutline,
  sunnyOutline,
  calendarNumberOutline,
  addCircleOutline,
  removeCircleOutline,
  star,
  addOutline,
  hourglassOutline,
  playOutline,
} from 'ionicons/icons';
import { format, addDays, startOfWeek, addWeeks, isSameDay } from 'date-fns';
import { Modal, ModalSection } from '../ui/Modal';
import { TextInput } from '../ui/TextInput';
import { Button } from '../ui/Button';
import { Stack, Row } from '../ui/Layout';
import { IconPickerModal, ClickableIcon, type IconValue } from '../ui/IconPicker';
import { supabase } from '../../services/supabase';
import type { TaskAssignment, Task, FamilyMember } from '../../types';
import './tasks.css';

const ATTACHMENTS_BUCKET = 'task-attachments';

export interface TaskAttachment {
  id: string;
  name: string;
  url: string;
  type: string; // MIME type
  size: number;
  uploaded_at: string;
}

export interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: TaskAssignment | null;
  task?: Task | null;
  familyMembers: FamilyMember[];
  householdId: string;
  onUpdate: (data: {
    assignedTo?: string;
    dueDate?: string;
    dueTime?: string;
    startDate?: string;
    startTime?: string;
    notes?: string;
    attachments?: TaskAttachment[];
    icon?: string;
    pointsValue?: number;
    taskName?: string;
  }) => Promise<boolean>;
  onDelete?: () => Promise<void>;
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  onClose,
  assignment,
  task: propTask,
  familyMembers,
  householdId,
  onUpdate,
  onDelete,
}) => {
  // Get task from assignment or prop
  const task = assignment?.task || propTask;

  // Form state
  const [taskName, setTaskName] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [dueTime, setDueTime] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [taskIcon, setTaskIcon] = useState<string>('');
  const [pointsValue, setPointsValue] = useState<number>(0);

  // UI state
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showPointsEditor, setShowPointsEditor] = useState(false);
  const [showDueDateSection, setShowDueDateSection] = useState(false);
  const [showStartDateSection, setShowStartDateSection] = useState(false);
  const [showDurationSection, setShowDurationSection] = useState(false);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Load data when modal opens or assignment changes
  useEffect(() => {
    if (isOpen && assignment) {
      setTaskName(task?.name || '');
      setAssignedTo(assignment.assigned_to || '');
      setDueDate(assignment.due_date || '');
      setDueTime(assignment.due_time || assignment.scheduled_time || '');
      setStartDate(assignment.actual_start_time?.split('T')[0] || '');
      setStartTime(assignment.actual_start_time?.split('T')[1]?.slice(0, 5) || '');
      setNotes(assignment.notes || '');
      // Load icon and points from task
      setTaskIcon(task?.icon || '');
      setPointsValue(task?.points_value || 0);
      // Load attachments from assignment metadata if stored there
      // For now, initialize empty - would load from a separate table in production
      setAttachments([]);
      setHasChanges(false);
      // Reset UI states when opening with new assignment
      setShowDeleteAlert(false);
      setShowIconPicker(false);
      setSaving(false);
      setDeleting(false);
      setShowPointsEditor(false);
      // Show sections if they have data
      setShowDueDateSection(!!(assignment.due_date || assignment.due_time || assignment.scheduled_time));
      setShowStartDateSection(!!(assignment.actual_start_time));
      setShowDurationSection(false); // Duration is not stored yet
    }
  }, [isOpen, assignment, task]);

  // Reset alert state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowDeleteAlert(false);
      setShowIconPicker(false);
    }
  }, [isOpen]);

  // Track changes
  useEffect(() => {
    if (!assignment) return;

    const originalTaskName = task?.name || '';
    const originalDueDate = assignment.due_date || '';
    const originalDueTime = assignment.due_time || assignment.scheduled_time || '';
    const originalNotes = assignment.notes || '';
    const originalAssignedTo = assignment.assigned_to || '';
    const originalIcon = task?.icon || '';
    const originalPoints = task?.points_value || 0;

    const changed =
      taskName !== originalTaskName ||
      dueDate !== originalDueDate ||
      dueTime !== originalDueTime ||
      notes !== originalNotes ||
      assignedTo !== originalAssignedTo ||
      startDate !== (assignment.actual_start_time?.split('T')[0] || '') ||
      startTime !== (assignment.actual_start_time?.split('T')[1]?.slice(0, 5) || '') ||
      attachments.length > 0 ||
      taskIcon !== originalIcon ||
      pointsValue !== originalPoints;

    setHasChanges(changed);
  }, [assignment, task, taskName, dueDate, dueTime, notes, assignedTo, startDate, startTime, attachments, taskIcon, pointsValue]);

  // Member chips for selection
  const memberChips = useMemo(() =>
    familyMembers
      .filter(m => m.member_type !== 'pet')
      .map(m => ({
        id: m.id,
        name: m.nickname || m.name,
        avatar: m.avatar_url,
        emoji: m.avatar_emoji || '👤',
        color: m.color,
      })),
  [familyMembers]);

  // File upload handler
  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || !assignment) return;

    setUploading(true);
    const newAttachments: TaskAttachment[] = [];

    try {
      for (const file of Array.from(files)) {
        // Generate unique filename
        const ext = file.name.split('.').pop() || 'bin';
        const filename = `${householdId}/${assignment.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

        // Upload to Supabase Storage
        const { data, error } = await supabase.storage
          .from(ATTACHMENTS_BUCKET)
          .upload(filename, file, {
            contentType: file.type,
            cacheControl: '3600',
          });

        if (error) {
          console.error('Upload error:', error);
          continue;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from(ATTACHMENTS_BUCKET)
          .getPublicUrl(data.path);

        newAttachments.push({
          id: data.path,
          name: file.name,
          url: urlData.publicUrl,
          type: file.type,
          size: file.size,
          uploaded_at: new Date().toISOString(),
        });
      }

      if (newAttachments.length > 0) {
        setAttachments(prev => [...prev, ...newAttachments]);
      }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploading(false);
    }
  }, [assignment, householdId]);

  // Delete attachment handler
  const handleDeleteAttachment = useCallback(async (attachment: TaskAttachment) => {
    try {
      // Delete from storage
      await supabase.storage
        .from(ATTACHMENTS_BUCKET)
        .remove([attachment.id]);

      // Remove from state
      setAttachments(prev => prev.filter(a => a.id !== attachment.id));
    } catch (err) {
      console.error('Error deleting attachment:', err);
    }
  }, []);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get file icon based on type
  const getFileIcon = (type: string): string => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('document') || type.includes('word')) return '📝';
    if (type.includes('spreadsheet') || type.includes('excel')) return '📊';
    return '📎';
  };

  // Handle icon selection from picker
  const handleIconSelect = useCallback((iconValue: IconValue) => {
    // Convert IconValue to string (emoji or icon name)
    if (iconValue.type === 'emoji') {
      setTaskIcon(iconValue.name);
    } else {
      // For non-emoji icons, store as type:name format
      setTaskIcon(`${iconValue.type}:${iconValue.name}`);
    }
  }, []);

  // Parse icon string back to IconValue for the picker
  const parseIconValue = useCallback((iconStr: string): IconValue | null => {
    if (!iconStr) return null;

    // Check if it's a typed icon (e.g., "ionic:home-outline")
    if (iconStr.includes(':')) {
      const [type, name] = iconStr.split(':');
      return { type: type as IconValue['type'], name };
    }

    // Otherwise treat as emoji
    return { type: 'emoji', name: iconStr };
  }, []);

  // Quick date selection helpers
  const getNextMonday = useCallback(() => {
    const today = new Date();
    const nextWeek = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1);
    return nextWeek;
  }, []);

  const quickDateOptions = useMemo(() => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const nextMonday = getNextMonday();

    return [
      { label: 'Today', date: today, icon: todayOutline },
      { label: 'Tomorrow', date: tomorrow, icon: sunnyOutline },
      { label: 'Next Week', date: nextMonday, icon: calendarNumberOutline },
    ];
  }, [getNextMonday]);

  // Check if a date matches one of the quick options
  const getSelectedQuickDate = useCallback((dateStr: string) => {
    if (!dateStr) return null;
    // Parse date string as local date (not UTC) by splitting the string
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    const nextMonday = getNextMonday();

    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, addDays(today, 1))) return 'Tomorrow';
    if (isSameDay(date, nextMonday)) return 'Next Week';
    return null;
  }, [getNextMonday]);

  // Handle quick date selection
  const handleQuickDateSelect = useCallback((date: Date) => {
    setDueDate(format(date, 'yyyy-MM-dd'));
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!assignment) return;

    setSaving(true);
    try {
      const success = await onUpdate({
        taskName: taskName.trim() || undefined,
        assignedTo: assignedTo || undefined,
        dueDate: dueDate || undefined,
        dueTime: dueTime || undefined,
        startDate: startDate || undefined,
        startTime: startTime || undefined,
        notes: notes || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        icon: taskIcon || undefined,
        pointsValue: pointsValue > 0 ? pointsValue : undefined,
      });

      if (success) {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle delete - shows confirmation alert
  const handleDeleteClick = () => {
    setShowDeleteAlert(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!onDelete) return;

    setDeleting(true);
    try {
      await onDelete();
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  if (!assignment || !task) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        header={{ title: 'Edit Task' }}
        footer={{
          primaryAction: {
            label: saving ? 'Saving...' : 'Save Changes',
            onClick: handleSave,
            disabled: !hasChanges || saving || !taskName.trim(),
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: onClose,
          },
          tertiaryAction: onDelete ? {
            label: deleting ? 'Deleting...' : 'Delete',
            onClick: handleDeleteClick,
            disabled: deleting,
            variant: 'danger',
          } : undefined,
        }}
      >
        <ModalSection spacing="normal">
          <Stack gap="md">
            {/* Task Info Header */}
            <div className="task-detail__header">
              <ClickableIcon
                icon={parseIconValue(taskIcon)}
                fallback="📋"
                size="lg"
                onClick={() => setShowIconPicker(true)}
                showEditOverlay
              />
              <div className="task-detail__info">
                <TextInput
                  value={taskName}
                  onChange={setTaskName}
                  placeholder="Task name..."
                  variant="default"
                  size="lg"
                  className="task-detail__name-input"
                />
                {task.description && (
                  <p className="task-detail__description">{task.description}</p>
                )}
              </div>
            </div>

            {/* Assignment To */}
            <div>
              <div className="task-detail__label">Assigned To</div>
              <div className="task-detail__members">
                {memberChips.map(member => (
                  <button
                    key={member.id}
                    type="button"
                    className={`task-detail__member-chip ${assignedTo === member.id ? 'active' : ''}`}
                    onClick={() => setAssignedTo(member.id)}
                    style={{ '--member-color': member.color } as React.CSSProperties}
                  >
                    {member.avatar ? (
                      <img src={member.avatar} alt="" className="task-detail__member-avatar" />
                    ) : (
                      <span className="task-detail__member-emoji">{member.emoji}</span>
                    )}
                    <span className="task-detail__member-name">{member.name}</span>
                    {assignedTo === member.id && (
                      <IonIcon icon={checkmark} className="task-detail__member-check" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════
                ACTION CHIPS - Collapsible sections for task details
                ═══════════════════════════════════════════════════════════ */}
            <div className="task-detail__actions">
              {/* Due Date Chip */}
              {!showDueDateSection && !dueDate ? (
                <button
                  type="button"
                  className="task-detail__action-chip"
                  onClick={() => setShowDueDateSection(true)}
                >
                  <IonIcon icon={addOutline} className="task-detail__action-chip-add" />
                  <span>Due date</span>
                  <IonIcon icon={calendarOutline} />
                </button>
              ) : (
                <button
                  type="button"
                  className="task-detail__action-chip task-detail__action-chip--active"
                  onClick={() => setShowDueDateSection(!showDueDateSection)}
                >
                  <IonIcon icon={calendarOutline} />
                  <span>
                    {dueDate ? (() => {
                      const [year, month, day] = dueDate.split('-').map(Number);
                      const date = new Date(year, month - 1, day);
                      return format(date, 'EEE, MMM d');
                    })() : 'Due date'}
                    {dueTime && ` at ${dueTime}`}
                  </span>
                </button>
              )}

              {/* Start Date Chip */}
              {!showStartDateSection && !startDate ? (
                <button
                  type="button"
                  className="task-detail__action-chip"
                  onClick={() => setShowStartDateSection(true)}
                >
                  <IonIcon icon={addOutline} className="task-detail__action-chip-add" />
                  <span>Start date</span>
                  <IonIcon icon={playOutline} />
                </button>
              ) : (
                <button
                  type="button"
                  className="task-detail__action-chip task-detail__action-chip--active"
                  onClick={() => setShowStartDateSection(!showStartDateSection)}
                >
                  <IonIcon icon={playOutline} />
                  <span>
                    {startDate ? (() => {
                      const [year, month, day] = startDate.split('-').map(Number);
                      const date = new Date(year, month - 1, day);
                      return format(date, 'EEE, MMM d');
                    })() : 'Start date'}
                    {startTime && ` at ${startTime}`}
                  </span>
                </button>
              )}

              {/* Duration Chip */}
              <button
                type="button"
                className="task-detail__action-chip"
                onClick={() => setShowDurationSection(!showDurationSection)}
              >
                <IonIcon icon={addOutline} className="task-detail__action-chip-add" />
                <span>Duration</span>
                <IonIcon icon={hourglassOutline} />
              </button>

              {/* Points Chip */}
              {!showPointsEditor && pointsValue === 0 ? (
                <button
                  type="button"
                  className="task-detail__action-chip"
                  onClick={() => setShowPointsEditor(true)}
                >
                  <IonIcon icon={addOutline} className="task-detail__action-chip-add" />
                  <span>Stars</span>
                  <IonIcon icon={starOutline} />
                </button>
              ) : (
                <button
                  type="button"
                  className="task-detail__action-chip task-detail__action-chip--gold"
                  onClick={() => setShowPointsEditor(!showPointsEditor)}
                >
                  <IonIcon icon={star} />
                  <span>{pointsValue} {pointsValue === 1 ? 'star' : 'stars'}</span>
                </button>
              )}

            </div>

            {/* ═══════════════════════════════════════════════════════════
                EXPANDABLE SECTIONS
                ═══════════════════════════════════════════════════════════ */}

            {/* Due Date Section */}
            {showDueDateSection && (
              <div className="task-detail__expandable-section">
                <div className="task-detail__section-header">
                  <h4><IonIcon icon={calendarOutline} /> Due Date</h4>
                  <button
                    type="button"
                    className="task-detail__section-close"
                    onClick={() => {
                      if (!dueDate && !dueTime) setShowDueDateSection(false);
                      else {
                        setDueDate('');
                        setDueTime('');
                        setShowDueDateSection(false);
                      }
                    }}
                    title="Remove"
                  >
                    <IonIcon icon={close} />
                  </button>
                </div>
                {/* Quick date options as cards */}
                <div className="task-detail__date-options">
                  {quickDateOptions.map(option => (
                    <button
                      key={option.label}
                      type="button"
                      className={`task-detail__date-option ${getSelectedQuickDate(dueDate) === option.label ? 'active' : ''}`}
                      onClick={() => handleQuickDateSelect(option.date)}
                    >
                      <IonIcon icon={option.icon} className="task-detail__date-option-icon" />
                      <span className="task-detail__date-option-label">{option.label}</span>
                      <span className="task-detail__date-option-date">
                        {format(option.date, 'MMM d')}
                      </span>
                    </button>
                  ))}
                  <div className="task-detail__picker-wrapper">
                    <button
                      type="button"
                      className={`task-detail__date-option ${dueDate && !getSelectedQuickDate(dueDate) ? 'active' : ''}`}
                      onClick={() => {
                        const input = document.getElementById('task-due-date-input') as HTMLInputElement;
                        if (input) input.showPicker?.();
                      }}
                      title="Pick a date"
                    >
                      <IonIcon icon={calendarNumberOutline} className="task-detail__date-option-icon" />
                      <span className="task-detail__date-option-label">Custom</span>
                      <span className="task-detail__date-option-date">Pick date</span>
                    </button>
                    <input
                      id="task-due-date-input"
                      type="date"
                      className="task-detail__hidden-date-input"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                  </div>
                </div>
                {/* Time picker row */}
                <div className="task-detail__time-row">
                  <div className="task-detail__picker-wrapper" style={{ flex: 1 }}>
                    <button
                      type="button"
                      className={`task-detail__time-option ${dueTime ? 'active' : ''}`}
                      onClick={() => {
                        const input = document.getElementById('task-due-time-input') as HTMLInputElement;
                        if (input) input.showPicker?.();
                      }}
                    >
                      <IonIcon icon={timeOutline} />
                      <span>{dueTime || 'Add time'}</span>
                    </button>
                    <input
                      id="task-due-time-input"
                      type="time"
                      className="task-detail__hidden-date-input"
                      value={dueTime}
                      onChange={(e) => setDueTime(e.target.value)}
                    />
                  </div>
                </div>
                {/* Selected date summary */}
                {dueDate && (
                  <div className="task-detail__date-summary">
                    <span className="task-detail__date-summary-label">Selected:</span>
                    <span className="task-detail__date-summary-value">
                      {(() => {
                        const [year, month, day] = dueDate.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return format(date, 'EEEE, MMMM d, yyyy');
                      })()}
                      {dueTime && ` at ${dueTime}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Start Date Section */}
            {showStartDateSection && (
              <div className="task-detail__expandable-section">
                <div className="task-detail__section-header">
                  <h4><IonIcon icon={playOutline} /> Start Date</h4>
                  <button
                    type="button"
                    className="task-detail__section-close"
                    onClick={() => {
                      setStartDate('');
                      setStartTime('');
                      setShowStartDateSection(false);
                    }}
                    title="Remove"
                  >
                    <IonIcon icon={close} />
                  </button>
                </div>
                {/* Quick date options as cards */}
                <div className="task-detail__date-options">
                  {quickDateOptions.map(option => (
                    <button
                      key={option.label}
                      type="button"
                      className={`task-detail__date-option ${getSelectedQuickDate(startDate) === option.label ? 'active' : ''}`}
                      onClick={() => setStartDate(format(option.date, 'yyyy-MM-dd'))}
                    >
                      <IonIcon icon={option.icon} className="task-detail__date-option-icon" />
                      <span className="task-detail__date-option-label">{option.label}</span>
                      <span className="task-detail__date-option-date">
                        {format(option.date, 'MMM d')}
                      </span>
                    </button>
                  ))}
                  <div className="task-detail__picker-wrapper">
                    <button
                      type="button"
                      className={`task-detail__date-option ${startDate && !getSelectedQuickDate(startDate) ? 'active' : ''}`}
                      onClick={() => {
                        const input = document.getElementById('task-start-date-input') as HTMLInputElement;
                        if (input) input.showPicker?.();
                      }}
                      title="Pick a date"
                    >
                      <IonIcon icon={calendarNumberOutline} className="task-detail__date-option-icon" />
                      <span className="task-detail__date-option-label">Custom</span>
                      <span className="task-detail__date-option-date">Pick date</span>
                    </button>
                    <input
                      id="task-start-date-input"
                      type="date"
                      className="task-detail__hidden-date-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                {/* Time picker row */}
                <div className="task-detail__time-row">
                  <div className="task-detail__picker-wrapper" style={{ flex: 1 }}>
                    <button
                      type="button"
                      className={`task-detail__time-option ${startTime ? 'active' : ''}`}
                      onClick={() => {
                        const input = document.getElementById('task-start-time-input') as HTMLInputElement;
                        if (input) input.showPicker?.();
                      }}
                    >
                      <IonIcon icon={timeOutline} />
                      <span>{startTime || 'Add time'}</span>
                    </button>
                    <input
                      id="task-start-time-input"
                      type="time"
                      className="task-detail__hidden-date-input"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                </div>
                {/* Selected date summary */}
                {startDate && (
                  <div className="task-detail__date-summary">
                    <span className="task-detail__date-summary-label">Selected:</span>
                    <span className="task-detail__date-summary-value">
                      {(() => {
                        const [year, month, day] = startDate.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return format(date, 'EEEE, MMMM d, yyyy');
                      })()}
                      {startTime && ` at ${startTime}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Duration Section (placeholder) */}
            {showDurationSection && (
              <div className="task-detail__expandable-section">
                <div className="task-detail__section-header">
                  <h4><IonIcon icon={hourglassOutline} /> Duration</h4>
                  <button
                    type="button"
                    className="task-detail__section-close"
                    onClick={() => setShowDurationSection(false)}
                    title="Close"
                  >
                    <IonIcon icon={close} />
                  </button>
                </div>
                <div className="task-detail__duration-hint">
                  Duration tracking coming soon
                </div>
              </div>
            )}

            {/* Points Section */}
            {showPointsEditor && (
              <div className="task-detail__expandable-section task-detail__expandable-section--gold">
                <div className="task-detail__section-header">
                  <h4><IonIcon icon={star} /> Stars</h4>
                  <button
                    type="button"
                    className="task-detail__section-close"
                    onClick={() => {
                      if (pointsValue === 0) setShowPointsEditor(false);
                      else {
                        setPointsValue(0);
                        setShowPointsEditor(false);
                      }
                    }}
                    title="Remove"
                  >
                    <IonIcon icon={close} />
                  </button>
                </div>
                <div className="task-detail__points-stepper">
                  <button
                    type="button"
                    className="task-detail__points-btn task-detail__points-btn--minus"
                    onClick={() => setPointsValue(Math.max(0, pointsValue - 1))}
                    disabled={pointsValue <= 0}
                  >
                    <IonIcon icon={removeCircleOutline} />
                  </button>
                  <div className="task-detail__points-value">
                    <IonIcon icon={star} className="task-detail__points-star" />
                    <span>{pointsValue}</span>
                  </div>
                  <button
                    type="button"
                    className="task-detail__points-btn task-detail__points-btn--plus"
                    onClick={() => setPointsValue(Math.min(100, pointsValue + 1))}
                    disabled={pointsValue >= 100}
                  >
                    <IonIcon icon={addCircleOutline} />
                  </button>
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
                ALWAYS VISIBLE SECTIONS - Notes & Attachments
                ═══════════════════════════════════════════════════════════ */}

            {/* Notes Section - Always visible */}
            <div className="task-detail__static-section">
              <div className="task-detail__section-header">
                <h4><IonIcon icon={documentTextOutline} /> Notes</h4>
              </div>
              <TextInput
                value={notes}
                onChange={setNotes}
                placeholder="Add a note..."
                variant="default"
                size="md"
                multiline
                rows={3}
              />
            </div>

            {/* Attachments Section - Always visible */}
            <div className="task-detail__static-section">
              <div className="task-detail__section-header">
                <h4><IonIcon icon={attachOutline} /> Attachments</h4>
              </div>
              {attachments.length > 0 && (
                <div className="task-detail__attachment-list">
                  {attachments.map(attachment => (
                    <div key={attachment.id} className="task-detail__attachment-item">
                      <span className="task-detail__attachment-icon">
                        {getFileIcon(attachment.type)}
                      </span>
                      <div className="task-detail__attachment-info">
                        <span className="task-detail__attachment-name">{attachment.name}</span>
                        <span className="task-detail__attachment-size">
                          {formatFileSize(attachment.size)}
                        </span>
                      </div>
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="task-detail__attachment-action"
                        title="Download"
                      >
                        <IonIcon icon={downloadOutline} />
                      </a>
                      <button
                        type="button"
                        className="task-detail__attachment-action task-detail__attachment-delete"
                        onClick={() => handleDeleteAttachment(attachment)}
                        title="Delete"
                      >
                        <IonIcon icon={trashOutline} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="task-detail__attachment-add-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <IonIcon icon={attachOutline} />
                <span>{uploading ? 'Uploading...' : 'Add attachment'}</span>
              </button>
            </div>

            {/* Hidden File Inputs */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => handleFileUpload(e.target.files)}
              style={{ display: 'none' }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFileUpload(e.target.files)}
              style={{ display: 'none' }}
            />

            {/* Status Info */}
            {assignment.is_completed && (
              <div className="task-detail__status task-detail__status--completed">
                <IonIcon icon={checkmark} />
                Completed {assignment.completed_at ? `on ${new Date(assignment.completed_at).toLocaleDateString()}` : ''}
              </div>
            )}
          </Stack>
        </ModalSection>
      </Modal>

      {/* Icon Picker Modal */}
      <IconPickerModal
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSave={handleIconSelect}
        currentIcon={parseIconValue(taskIcon)}
        title="Choose Task Icon"
        subtitle="Select an icon to represent this task"
        defaultTab="emoji"
      />

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Delete Task"
        message="Are you sure you want to delete this task assignment? This cannot be undone."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Delete',
            role: 'destructive',
            handler: handleDeleteConfirm,
          },
        ]}
      />
    </>
  );
};

TaskDetailModal.displayName = 'TaskDetailModal';

export default TaskDetailModal;
